export const CONSENT_KEY = "apexav-advertising-consent";
export const LEGACY_KEY = "apexav-cookie-consent";
export const CONSENT_EVENT = "apexav-consent-change";
export const SETTINGS_EVENT = "apexav-cookie-settings";
export const NOTICE_VERSION = "2026-10-20";
export const CONSENT_NOTICE = "Google receives device, page, approximate location and cookie data for advertising measurement and optimization when permitted. Analytics requires acceptance. Declining stops optional Google tracking. Change your choice through Cookie settings.";

const CONSENT_COUNTRIES = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE", "IS", "LI", "NO", "GB", "CH", "CA",
]);
export type ConsentRegion = "required" | "ordinary" | "unknown";
export type ConsentChoice = "accepted" | "declined" | null;
type ConsentRecord = {
  visitorId: string;
  choice: Exclude<ConsentChoice, null>;
  region: ConsentRegion;
  recordedAt: string;
  noticeVersion: string;
  notice: string;
};

export function regionFromTrace(trace: string): ConsentRegion {
  const country = trace.match(/^loc=([A-Z0-9]{2})$/m)?.[1];
  if (!country || country === "XX" || country === "T1") return "unknown";
  return CONSENT_COUNTRIES.has(country) ? "required" : "ordinary";
}

export function advertisingAllowed(choice: ConsentChoice, region: ConsentRegion): boolean {
  if (choice !== null) return choice === "accepted";
  return region === "ordinary";
}

export function readConsent(): ConsentChoice {
  try {
    const record = JSON.parse(localStorage.getItem(CONSENT_KEY) || "null");
    if (record?.choice === "declined") return "declined";
    if (record?.choice === "accepted" && record.noticeVersion === NOTICE_VERSION) return "accepted";
    // An old analytics acceptance did not answer the advertising question.
    return localStorage.getItem(LEGACY_KEY) === "declined" ? "declined" : null;
  } catch {
    return null;
  }
}

export async function lookupRegion(): Promise<ConsentRegion> {
  try {
    const response = await fetch("/cdn-cgi/trace", { signal: AbortSignal.timeout(2000) });
    return response.ok ? regionFromTrace(await response.text()) : "unknown";
  } catch {
    return "unknown";
  }
}

export function saveConsent(choice: Exclude<ConsentChoice, null>, region: ConsentRegion): boolean {
  try {
    const previous = JSON.parse(localStorage.getItem(CONSENT_KEY) || "null");
    const record: ConsentRecord = {
      visitorId: typeof previous?.visitorId === "string" ? previous.visitorId : crypto.randomUUID(),
      choice, region, recordedAt: new Date().toISOString(), noticeVersion: NOTICE_VERSION, notice: CONSENT_NOTICE,
    };
    const history = JSON.parse(localStorage.getItem(`${CONSENT_KEY}-history`) || "[]");
    localStorage.setItem(`${CONSENT_KEY}-history`, JSON.stringify([...(Array.isArray(history) ? history : []), record]));
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
    localStorage.setItem(LEGACY_KEY, choice);
    window.dispatchEvent(new Event(CONSENT_EVENT));
    return true;
  } catch {
    return false;
  }
}

type TrackingWindow = Window & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
function googleCommand(...args: unknown[]) {
  const trackingWindow = window as TrackingWindow;
  trackingWindow.dataLayer ??= [];
  trackingWindow.gtag ??= (...values: unknown[]) => trackingWindow.dataLayer?.push(values);
  trackingWindow.gtag(...args);
}

let initialized = false;
export function syncGoogleConsent(choice: ConsentChoice, region: ConsentRegion) {
  const ads = advertisingAllowed(choice, region);
  const analytics = choice === "accepted";
  const signals = {
    ad_storage: ads ? "granted" : "denied",
    ad_user_data: ads ? "granted" : "denied",
    ad_personalization: ads ? "granted" : "denied",
    analytics_storage: analytics ? "granted" : "denied",
  };
  if (!initialized) {
    googleCommand("consent", "default", signals);
    initialized = true;
  } else {
    googleCommand("consent", "update", signals);
  }
  // Existing GTM tags must respect these signals; a refused/unknown visit
  // never loads the container, so no cookieless Google pings are enabled.
  const existing = document.querySelector("script[data-apex-google]");
  if (!ads && !analytics) {
    googleCommand("set", "user_data", null);
    // Reload unloads arbitrary container scripts; signals are denied first.
    if (existing) window.location.reload();
    return;
  }
  if (existing) return;
  const script = document.createElement("script");
  script.async = true;
  script.dataset.apexGoogle = "";
  script.src = "https://www.googletagmanager.com/gtm.js?id=GTM-WXDLPM7R&l=dataLayer";
  googleCommand("set", "developer_id.dZjgwMW", true);
  const trackingWindow = window as TrackingWindow;
  trackingWindow.dataLayer?.push({ "gtm.start": Date.now(), event: "gtm.js" });
  document.head.appendChild(script);
}

export function openCookieSettings() {
  window.dispatchEvent(new Event(SETTINGS_EVENT));
}